import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Check } from 'lucide-react';
import { ContactlessHostResults, getFieldTypeSpec } from '@cloudsign/shared';
import type { Field, KioskNoticesStepConfig } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Alert, Button, TextField } from '../../components/ui';
import { FieldInput, checkAnswer, rawFromValue } from '../FieldInput';
import type { Raw } from '../FieldInput';
import { NoticeView } from '../fields-notices/noticeText';
import { makePhotoPair } from '../public-links/photo';
import { toField } from '../public-links/PreArrivalForms';
import { SignatureInput } from './SignatureInput';
import type { SignatureAnswer } from './SignatureInput';
import { TYPED_SIGNATURE, call, problemText, uploadFile, uploadSignature } from './api';

/** Every step screen has the same shape: show something, call onNext(value) when the visitor is done. */
export interface StepProps<C, V> {
  /** Button text on the last screen ("Sign in" or "Sign out"); default "Next". */
  nextLabel?: string;
  config: C;
  session: string;
  busy?: boolean;
  onNext: (value: V) => void;
}

const BIG = 'min-h-12 w-full text-base';
const widgetOf = (f: Field) => getFieldTypeSpec(f.typeKey)?.widget ?? 'text';

// ---------- Host picker (names only, at least 2 letters, at most 10 results) ----------

export function HostPicker({ session, value, onChange }: { session: string; value: { id: string; name: string } | null; onChange: (v: { id: string; name: string } | null) => void }) {
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<{ id: string; fullName: string }[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-3">
        <span className="text-base">{t('w21.host.chosen', { name: value.name })}</span>
        <button type="button" className="min-h-11 px-2 text-base text-brand underline" onClick={() => onChange(null)}>
          {t('w21.host.change')}
        </button>
      </div>
    );
  }
  const search = async (q: string) => {
    setTerm(q);
    if (q.trim().length < 2) return setFound([]);
    try {
      const r = await call('GET', '/contactless/session/hosts', { session, query: { q: q.trim() }, schema: ContactlessHostResults });
      setFound(r.items);
      setProblem(null);
    } catch (e) {
      setProblem(problemText(e));
    }
  };
  return (
    <div className="space-y-2">
      <TextField label={t('w21.host.label')} hint={t('w21.host.hint')} value={term} autoComplete="off" onChange={(e) => void search(e.target.value)} />
      {problem ? <Alert>{problem}</Alert> : null}
      {found.length > 0 ? (
        <ul aria-label={t('w21.host.results')} className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {found.map((h) => (
            <li key={h.id}>
              <button type="button" className="block min-h-12 w-full px-3 py-2 text-left text-base hover:bg-slate-50" onClick={() => onChange({ id: h.id, name: h.fullName })}>
                {h.fullName}
              </button>
            </li>
          ))}
        </ul>
      ) : term.trim().length >= 2 ? (
        <p className="text-sm text-slate-700">{t('w21.host.none')}</p>
      ) : null}
    </div>
  );
}

// ---------- Questions (custom fields) ----------

type QuestionsConfig = { fields?: unknown[] };
type QuestionsValue = { answers: { fieldId: string; value: unknown }[] };

/** The questions a phone can ask: the host question is the host picker on the details page; signatures are drawn here. */
export function askableFields(config: QuestionsConfig): Field[] {
  return (config.fields ?? []).map(toField).filter((f): f is Field => f !== null && widgetOf(f) !== 'member_picker');
}

export function QuestionsStep({ config, session, busy, nextLabel, onNext }: StepProps<QuestionsConfig, QuestionsValue>) {
  const fields = askableFields(config);
  const [raw, setRaw] = useState<Record<string, Raw>>(() => Object.fromEntries(fields.map((f) => [f.id, rawFromValue(f, undefined)])));
  const [sig, setSig] = useState<Record<string, SignatureAnswer | null>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const next = async () => {
    const bad: Record<string, string> = {};
    const answers: { fieldId: string; value: unknown }[] = [];
    setWorking(true);
    setProblem(null);
    try {
      for (const f of fields) {
        if (widgetOf(f) === 'signature') {
          const s = sig[f.id];
          if (!s) {
            if (f.required) bad[f.id] = 'error.field_required';
            continue;
          }
          const fileId = await uploadSignature(session, s.kind === 'drawn' ? s.value : TYPED_SIGNATURE);
          answers.push({ fieldId: f.id, value: { fileId } });
          continue;
        }
        const r = checkAnswer(f, raw[f.id]);
        if (!r.ok) bad[f.id] = r.messageKey;
        else if (r.value !== null && r.value !== undefined) answers.push({ fieldId: f.id, value: r.value });
      }
      setErrors(bad);
      if (Object.keys(bad).length === 0) onNext({ answers });
    } catch (e) {
      setProblem(problemText(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-4">
      {fields.map((f) =>
        widgetOf(f) === 'signature' ? (
          <div key={f.id} className="space-y-1">
            <SignatureInput label={f.required ? `${f.label} *` : f.label} disabled={working} onChange={(a) => setSig((p) => ({ ...p, [f.id]: a }))} />
            {errors[f.id] ? <p className="text-sm text-red-700">{t(errors[f.id] as string)}</p> : null}
          </div>
        ) : (
          <FieldInput key={f.id} field={f} raw={raw[f.id]} error={errors[f.id]} onChange={(v) => setRaw((p) => ({ ...p, [f.id]: v }))} />
        ),
      )}
      {problem ? <Alert>{problem}</Alert> : null}
      <Button variant="primary" className={BIG} disabled={busy || working} onClick={() => void next()}>
        {nextLabel ?? t('w21.next')}
      </Button>
    </div>
  );
}

// ---------- Notices (read, accept, sign) ----------

type Notice = KioskNoticesStepConfig['notices'][number];
type NoticesValue = { acceptances: { noticeVersionId: string; acceptedAt: string; shownSha256: string; signatureUploadId: string | null }[] };

export function NoticesStep({ config, session, busy, nextLabel, onNext }: StepProps<KioskNoticesStepConfig, NoticesValue>) {
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});
  const [sig, setSig] = useState<Record<string, SignatureAnswer | null>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const next = async () => {
    const needAgree = config.notices.find((n) => n.requiresAcceptance && !agreed[n.id]);
    if (needAgree) return setProblem(t('w21.notices.needAgree', { title: needAgree.title }));
    const needSign = config.notices.find((n) => n.requireSignature && !sig[n.id]);
    if (needSign) return setProblem(t('w21.notices.needSign', { title: needSign.title }));
    setWorking(true);
    setProblem(null);
    try {
      const acceptances: NoticesValue['acceptances'] = [];
      for (const n of config.notices) {
        const s = sig[n.id];
        const signatureUploadId = n.requireSignature && s ? await uploadSignature(session, s.kind === 'drawn' ? s.value : TYPED_SIGNATURE) : null;
        acceptances.push({ noticeVersionId: n.id, acceptedAt: new Date().toISOString(), shownSha256: n.contentSha256, signatureUploadId });
      }
      onNext({ acceptances });
    } catch (e) {
      setProblem(problemText(e));
    } finally {
      setWorking(false);
    }
  };

  const item = (n: Notice) => (
    <div key={n.id} className="space-y-3 rounded-md border border-slate-200 p-3">
      <h3 className="text-lg font-semibold">{n.title}</h3>
      <div className="max-h-72 overflow-y-auto rounded bg-slate-50 p-3">
        <NoticeView body={n.body} />
      </div>
      {n.requiresAcceptance ? (
        <label className="flex min-h-12 items-start gap-3 text-base">
          <input
            type="checkbox"
            className="mt-1 h-6 w-6 shrink-0 accent-[var(--brand-primary)]"
            checked={agreed[n.id] === true}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setAgreed((p) => ({ ...p, [n.id]: e.target.checked }))}
          />
          {t('w21.notices.agree')}
        </label>
      ) : null}
      {n.requireSignature ? <SignatureInput label={t('w21.notices.signHere')} disabled={working} onChange={(a) => setSig((p) => ({ ...p, [n.id]: a }))} /> : null}
    </div>
  );

  return (
    <div className="space-y-4">
      {config.notices.map(item)}
      {problem ? <Alert>{problem}</Alert> : null}
      <Button variant="primary" className={BIG} disabled={busy || working} onClick={() => void next()}>
        {nextLabel ?? t('w21.next')}
      </Button>
    </div>
  );
}

// ---------- Photo from the phone camera (asked for only on this step) ----------

type PhotoConfig = { mode?: 'always' | 'chance' };
type PhotoValue = { photoUploadId: string; thumbUploadId: string } | null;

export function PhotoStep({ config, session, busy, nextLabel, onNext }: StepProps<PhotoConfig, PhotoValue>) {
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState<PhotoValue>(null);
  const required = config.mode === 'always';

  const choose = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return setProblem(t('w21.photo.notImage'));
    setWorking(true);
    setProblem(null);
    try {
      const pair = await makePhotoPair(file);
      if (!pair) return setProblem(t('w21.photo.failed'));
      const photoUploadId = await uploadFile(session, 'photo', pair.photo);
      const thumbUploadId = await uploadFile(session, 'photo_thumb', pair.thumb);
      setDone({ photoUploadId, thumbUploadId });
    } catch (err) {
      setProblem(problemText(err));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-base text-slate-800">{t(required ? 'w21.photo.helpRequired' : 'w21.photo.help')}</p>
      {done ? (
        <p className="flex items-center gap-2 font-medium text-green-800">
          <Check aria-hidden="true" className="h-5 w-5" />
          {t('w21.photo.done')}
        </p>
      ) : null}
      <label className="inline-flex min-h-14 w-full cursor-pointer items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-lg font-medium has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-brand">
        {working ? t('w21.photo.working') : done ? t('w21.photo.retake') : t('w21.photo.take')}
        <input type="file" accept="image/*" capture="user" className="sr-only" disabled={working || busy} onChange={(e) => void choose(e)} />
      </label>
      {problem ? <Alert>{problem}</Alert> : null}
      <Button variant="primary" className={BIG} disabled={busy || working || (required && !done)} onClick={() => onNext(done)}>
        {done || required ? (nextLabel ?? t('w21.next')) : t('w21.skip')}
      </Button>
    </div>
  );
}
